import { api } from "@/api";

export type TicketLabel = {
  id: string;
  name: string;
  color: string;
};

export type TicketInput = {
  title: string;
  description: string | null;
  label_id: string | null;
};

export type TicketCreated = {
  id: string;
  title: string;
  /** Reply deadline (24 h), Brasília time without offset. */
  due_at: string;
};

export type Ticket = {
  id: string;
  title: string;
  description: string | null;
  /** Current column of the ticket on Fidexa's operation board. */
  status: string;
  is_resolved: boolean;
  is_overdue: boolean;
  /** Brasília time without offset. */
  due_at: string | null;
  created_at: string;
  requester_name: string | null;
  label_name: string | null;
  label_color: string | null;
  /** Status changed since the store last opened the list. */
  has_update: boolean;
  /** When the back office last moved the ticket (Brasília, no offset). */
  status_changed_at: string | null;
};

/** Tickets of the store's group (every user of the group sees the same). */
const list = async (): Promise<Ticket[]> => {
  const { data } = await api.get<Ticket[]>("v1/tickets");
  return data;
};

/** Labels a store can pick when opening a ticket (Melhoria, Erro, ...). */
const listLabels = async (): Promise<TicketLabel[]> => {
  const { data } = await api.get<TicketLabel[]>("v1/tickets/labels");
  return data;
};

/**
 * Opens a ticket for Fidexa: it becomes a card on the back-office operation
 * board, due in 24 h. User and group come from the login.
 */
const create = async (input: TicketInput): Promise<TicketCreated> => {
  const { data } = await api.post<TicketCreated>("v1/tickets", input);
  return data;
};

/** Group tickets whose status changed since the store last looked. */
const countUnread = async (): Promise<number> => {
  const { data } = await api.get<{ count: number }>("v1/tickets/unread");
  return data.count;
};

/**
 * The store saw these status changes: acknowledges exactly them, so a change
 * made after the list loaded still lights the dot.
 */
const markRead = async (tickets: Ticket[]): Promise<void> => {
  await api.post("v1/tickets/read", {
    items: tickets
      .filter(ticket => ticket.has_update && ticket.status_changed_at)
      .map(ticket => ({
        ticket_id: ticket.id,
        status_changed_at: ticket.status_changed_at,
      })),
  });
};

/** Image, video or file attached to the store's own ticket. */
const uploadAttachment = async (ticketId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  await api.post(`v1/tickets/${ticketId}/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const ticketService = {
  list,
  listLabels,
  create,
  countUnread,
  markRead,
  uploadAttachment,
};
